import React, { useState, useEffect } from 'react'

import { Card } from 'components'
import { useLoading, useModal, usePersistForm } from 'hooks'
import moment from 'moment'
import { useForm, FormProvider } from 'react-hook-form'
import { ContractServices } from 'services/NGT/Contract'
import { SPACING, FORMAT } from 'utils'
import { yup, yupResolver } from 'utils/yup'

import { FilterContracts, TableSearchContracts } from './components'
import { IForm } from './types'
import { hasConnectionLostError } from 'services/Instances/utils'

interface SearchSupplierContractsProps {
  handleClickContract: ({ _cdContrato, _contratoId }: any) => void
}

const schema = yup.object({
  DtFimSap: yup
    .string()
    .daterange({
      compare: 'DtInicioSap',
      message: 'Data cadastro fim deve ser maior que data cadastro inicio',
      period: 'end'
    })
    .date()
    .nullable(),
  DtInicioSap: yup
    .string()
    .date()
    .daterange({
      compare: 'DtFimSap',
      message: 'Data cadastro inicio deve ser menor que data cadastro fim',
      period: 'begin'
    })
    .date()
    .nullable()
})

const SearchSupplierContracts: React.FC<SearchSupplierContractsProps> = ({
  handleClickContract
}) => {
  const { showModal, showConnectionLostModal } = useModal()
  const { setLoading, setMsgErro } = useLoading()
  const formMethods = useForm<IForm>({
    defaultValues: {
      CdContrato: '',
      CdDocto: '',
      DtFimSap: null,
      DtInicioSap: null,
      NmContraparte: '',
      NmFornecedor: ''
    },
    resolver: yupResolver(schema)
  })
  usePersistForm('contratos-form', {
    setValue: formMethods.setValue,
    watch: formMethods.watch
  })

  const [contractTableData, setContractTableData] = useState<any[]>([])

  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [totalItems, setTotalItems] = useState<number>(0)

  const getContractTableData = async () => {
    setLoading('loading')

    const requestQUERY: any = {
      ...formMethods.getValues(),
      CdDocto: FORMAT.unMask(formMethods.getValues('CdDocto')),
      PageNumber: page,
      PageSize: rowsPerPage
    }

    const dtInicioSap = formMethods.getValues('DtInicioSap')
    const dtFimSap = formMethods.getValues('DtFimSap')

    if (dtInicioSap && dtFimSap) {
      requestQUERY.DtInicioSap = moment(dtInicioSap).format('YYYY-MM-DD')
      requestQUERY.DtFimSap = moment(dtFimSap).format('YYYY-MM-DD')
    }

    try {
      const response = await ContractServices().GetByParamsForGrid(requestQUERY)

      if (response.status !== 200) throw new Error(response.data.mensagem)
      setTotalItems(response.data.entity.pageCount)
      setContractTableData(response.data.entity.items)
      setLoading('idle')
    } catch (error: any) {
      setLoading('idle')
      if (hasConnectionLostError()) {
        showConnectionLostModal()
        return
      }
      showModal({
        hideButtonCancel: true,
        message: error.mensagem || error.message || 'Erro ao obter contratos.',
        txtButtonConfirm: 'Fechar',
        type: 'error'
      })
    }
  }

  const handleDeleteContract = (contractID: number, cdContrato: string) => {
    setLoading('loading')

    ContractServices()
      .VerificarContratoMovimentacao(contractID)
      .then((response) => {
        if (response.data?.sucesso) {
          showModal({
            hideButtonCancel: true,
            message: `Há movimentações no contrato! Não foi possível deletar o contrato ${cdContrato}!`,
            txtButtonConfirm: 'OK',
            type: 'error'
          })
        }

        if (!response.data?.sucesso) {
          ContractServices()
            .DeleteContractById(contractID)
            .then((response) => {
              if (response.status === 200) {
                showModal({
                  hideButtonCancel: true,
                  message: `O contrato ${cdContrato} foi removido com sucesso!`,
                  txtButtonConfirm: 'OK',
                  type: 'success'
                })
              }
              getContractTableData()
              setLoading('idle')
            })
            .catch(() => {
              setLoading('idle')
              showModal({
                hideButtonCancel: true,
                message: `Não foi possível deletar o contrato ${cdContrato}`,
                txtButtonConfirm: 'OK',
                type: 'error'
              })
            })
            .finally(() => setLoading('idle'))
        }
      })
    setLoading('idle')
  }

  const handleSearch = (form: IForm) => {
    setLoading('loading')
    setPage(1)
    const requestQUERY: any = {
      ...form,
      CdDocto: FORMAT.unMask(form.CdDocto),
      PageNumber: 1,
      PageSize: rowsPerPage
    }

    if (form.DtFimSap && form.DtInicioSap) {
      requestQUERY.DtInicioSap = moment(form.DtInicioSap).format('YYYY-MM-DD')
      requestQUERY.DtFimSap = moment(form.DtFimSap).format('YYYY-MM-DD')
    }

    ContractServices()
      .GetByParamsForGrid(requestQUERY)
      .then((response) => {
        const {
          data: {
            entity: { items: contractTableData, pageCount }
          }
        } = response
        setTotalItems(pageCount)

        if (response.status === 200) {
          setContractTableData(contractTableData)
        }

        setLoading('idle')
      })
      .catch((error) => {
        if (hasConnectionLostError()) {
          showConnectionLostModal()
          setLoading('idle')
          return
        }
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message:
            error.mensagem ||
            error.message ||
            'Não foi possível obter os dados desta pesquisa.',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  useEffect(() => {
    getContractTableData()
  }, [rowsPerPage, page])

  return (
    <FormProvider {...formMethods}>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FilterContracts handleSearch={handleSearch} />

        <TableSearchContracts
          handleDeleteContract={({ cdContrato, contratoId }) =>
            showModal({
              handleConfirm: () => handleDeleteContract(contratoId, cdContrato),
              message: `Tem certeza de que deseja apagar o contrato ${cdContrato}?`,
              txtButtonCancel: 'NÃO',
              txtButtonConfirm: 'SIM',
              type: 'alert'
            })
          }
          handleClickContract={handleClickContract}
          tableData={contractTableData}
          totalItems={totalItems}
          page={page}
          setPaginationDisabled={() => {
            return (
              [
                formMethods.getValues('DtFimSap'),
                formMethods.getValues('DtInicioSap')
              ].filter((value) => value == null).length === 1
            )
          }}
          setPage={setPage}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={setRowsPerPage}
        />
      </Card>
    </FormProvider>
  )
}

export default SearchSupplierContracts
